import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where
} from 'firebase/firestore';
import dayjs from 'dayjs';
import { db } from '../../firebase/config';
import { isOperatorRole } from './authSlice';
import {
  buildScheduleFields,
  findPlannedShiftConflicts,
  formatConflictMessage
} from '../../utils/plannedShiftTime';

// Doku: nextcab_mobile/docs/general/schichtplaner_ueberarbeitung.md

const getCurrentUser = (state) => state.auth?.user || null;

// Unternehmer-Konten sehen und planen nur Schichten ihrer eigenen Fahrer.
// Die Rules geben Listen-Abfragen nur mit operatorId-Filter frei.
const getOperatorScope = (state) => {
  const currentUser = getCurrentUser(state);
  return isOperatorRole(currentUser) ? currentUser.operatorId || null : null;
};

const withOperatorScope = (constraints, operatorScope) => (
  operatorScope ? [...constraints, where('operatorId', '==', operatorScope)] : constraints
);

// Fahrer- und Fahrzeugdokumente je Aufruf nur einmal lesen, auch wenn viele
// Schichtserien auf denselben Fahrer zeigen.
const createDocCache = () => {
  const cache = new Map();
  return (collectionName, id) => {
    if (!id) return Promise.resolve(null);
    const key = `${collectionName}/${id}`;
    if (!cache.has(key)) {
      cache.set(key, getDoc(doc(db, collectionName, id))
        .then((snap) => (snap.exists() ? snap.data() : null))
        .catch(() => null));
    }
    return cache.get(key);
  };
};

const loadDriverAndVehicle = async ({ driverId, vehicleId, operatorScope }) => {
  const [driverSnap, vehicleSnap] = await Promise.all([
    driverId ? getDoc(doc(db, 'users', driverId)) : Promise.resolve(null),
    vehicleId ? getDoc(doc(db, 'Vehicles', vehicleId)) : Promise.resolve(null)
  ]);

  if (!driverSnap?.exists() || !driverSnap.data().isDriver) {
    throw new Error('Fahrer nicht gefunden');
  }
  const driverData = driverSnap.data();

  let vehicleData = null;
  if (vehicleId) {
    if (!vehicleSnap?.exists()) {
      throw new Error('Fahrzeug nicht gefunden');
    }
    vehicleData = vehicleSnap.data();
    if (vehicleData.active === false) {
      throw new Error('Das Fahrzeug ist als Reserve deaktiviert und kann nicht eingeplant werden');
    }
  }

  if (operatorScope) {
    if ((driverData.operatorId || null) !== operatorScope) {
      throw new Error('Der Fahrer gehört zu einem anderen Unternehmer');
    }
    if (vehicleData && (vehicleData.operatorId || null) !== operatorScope) {
      throw new Error('Das Fahrzeug gehört zu einem anderen Unternehmer');
    }
  }

  return { driverData, vehicleData };
};

const loadActiveShifts = async (field, value, operatorScope) => {
  if (!value) return [];
  const snapshot = await getDocs(query(
    collection(db, 'plannedShifts'),
    ...withOperatorScope([
      where(field, '==', value),
      where('status', '==', 'active')
    ], operatorScope)
  ));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// Fahrer und Fahrzeug duerfen sich zeitlich mit keiner anderen aktiven
// Schicht ueberschneiden — auch nicht ueber Mitternacht hinweg.
const assertNoConflicts = async ({ candidate, ignoreId, operatorScope }) => {
  const [driverShifts, vehicleShifts] = await Promise.all([
    loadActiveShifts('driverId', candidate.driverId, operatorScope),
    loadActiveShifts('vehicleId', candidate.vehicleId, operatorScope)
  ]);

  const driverConflicts = findPlannedShiftConflicts(candidate, driverShifts, { ignoreId });
  if (driverConflicts.length > 0) {
    throw new Error(formatConflictMessage('Der Fahrer', driverConflicts));
  }

  const vehicleConflicts = findPlannedShiftConflicts(candidate, vehicleShifts, { ignoreId });
  if (vehicleConflicts.length > 0) {
    throw new Error(formatConflictMessage('Das Fahrzeug', vehicleConflicts));
  }
};

// Async thunks
export const fetchPlannedShifts = createAsyncThunk(
  'plannedShifts/fetchPlannedShifts',
  async ({ startDate, endDate, driverId } = {}, { rejectWithValue, getState }) => {
    try {
      const operatorScope = getOperatorScope(getState());
      const queryConstraints = [where('status', '==', 'active')];

      if (driverId) {
        queryConstraints.push(where('driverId', '==', driverId));
      }

      const querySnapshot = await getDocs(query(
        collection(db, 'plannedShifts'),
        ...withOperatorScope(queryConstraints, operatorScope)
      ));

      // Nachtschichten vom Vortag reichen in den Zeitraum hinein
      const rangeStart = startDate ? dayjs(startDate).subtract(1, 'day').format('YYYY-MM-DD') : null;
      const rangeEnd = endDate || null;

      // `dates` bleibt vollstaendig: Planer und Loeschdialog brauchen die
      // ganze Serie, die Ansichten filtern selbst nach Datum.
      const filteredDocs = querySnapshot.docs
        .map((shiftDoc) => ({ id: shiftDoc.id, data: shiftDoc.data() }))
        .filter(({ data }) => (
          !rangeStart && !rangeEnd
            ? true
            : (data.dates || []).some((date) => (
              (!rangeStart || date >= rangeStart) && (!rangeEnd || date <= rangeEnd)
            ))
        ));

      const loadDoc = createDocCache();
      const plannedShifts = await Promise.all(
        filteredDocs.map(async ({ id, data: shiftData }) => {
          const [driverData, vehicleData] = await Promise.all([
            loadDoc('users', shiftData.driverId),
            loadDoc('Vehicles', shiftData.vehicleId)
          ]);

          return {
            id,
            ...shiftData,
            driver: driverData?.isDriver ? driverData : null,
            vehicle: vehicleData
          };
        })
      );

      return plannedShifts;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPlannedShiftById = createAsyncThunk(
  'plannedShifts/fetchPlannedShiftById',
  async (shiftId, { rejectWithValue }) => {
    try {
      const shiftDoc = await getDoc(doc(db, 'plannedShifts', shiftId));
      
      if (!shiftDoc.exists()) {
        throw new Error('Schicht nicht gefunden');
      }
      
      const shiftData = shiftDoc.data();
      
      // Fetch driver data
      let driverData = null;
      if (shiftData.driverId) {
        const userDoc = await getDoc(doc(db, 'users', shiftData.driverId));
        if (userDoc.exists() && userDoc.data().isDriver) {
          driverData = userDoc.data();
        }
      }
      
      // Fetch vehicle data if assigned
      let vehicleData = null;
      if (shiftData.vehicleId) {
        const vehicleDoc = await getDoc(doc(db, 'Vehicles', shiftData.vehicleId));
        if (vehicleDoc.exists()) {
          vehicleData = vehicleDoc.data();
        }
      }
      
      return {
        id: shiftDoc.id,
        ...shiftData,
        driver: driverData,
        vehicle: vehicleData
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createPlannedShifts = createAsyncThunk(
  'plannedShifts/createPlannedShifts',
  async (shiftData, { rejectWithValue, getState }) => {
    try {
      const { driverId, vehicleId, dates, startTime, plannedDuration = 8, notes } = shiftData;
      const operatorScope = getOperatorScope(getState());

      const { driverData, vehicleData } = await loadDriverAndVehicle({ driverId, vehicleId, operatorScope });
      const schedule = buildScheduleFields({ dates, startTime, plannedDuration });

      await assertNoConflicts({
        candidate: { driverId, vehicleId, dates: schedule.dates, startTime, plannedDuration },
        operatorScope
      });

      const newPlannedShift = {
        driverId,
        driverName: driverData.name || '',
        // Mandant der Schicht = Unternehmer des Fahrers (Rules pruefen das)
        operatorId: driverData.operatorId || null,
        vehicleId: vehicleId || null,
        vehiclePlate: vehicleData?.licensePlate || null,
        startTime,
        plannedDuration,
        status: 'active',
        createdAt: new Date().toISOString(),
        createdBy: getCurrentUser(getState())?.uid || 'system',
        notes: notes || '',
        ...schedule
      };
      
      const docRef = await addDoc(collection(db, 'plannedShifts'), newPlannedShift);
      
      return {
        id: docRef.id,
        ...newPlannedShift,
        driver: driverData,
        vehicle: vehicleData
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updatePlannedShift = createAsyncThunk(
  'plannedShifts/updatePlannedShift',
  async ({ id, ...updateData }, { rejectWithValue, getState }) => {
    try {
      const operatorScope = getOperatorScope(getState());
      const shiftRef = doc(db, 'plannedShifts', id);
      
      const shiftDoc = await getDoc(shiftRef);
      if (!shiftDoc.exists()) {
        throw new Error('Geplante Schicht nicht gefunden');
      }
      const currentData = shiftDoc.data();

      const merged = {
        driverId: updateData.driverId ?? currentData.driverId,
        vehicleId: updateData.vehicleId ?? currentData.vehicleId ?? null,
        dates: updateData.dates ?? currentData.dates ?? [],
        startTime: updateData.startTime ?? currentData.startTime,
        plannedDuration: updateData.plannedDuration ?? currentData.plannedDuration ?? 8
      };

      const { driverData, vehicleData } = await loadDriverAndVehicle({ ...merged, operatorScope });
      const schedule = buildScheduleFields(merged);

      await assertNoConflicts({
        candidate: { ...merged, dates: schedule.dates },
        ignoreId: id,
        operatorScope
      });

      // Abgeleitete Felder (Name, Kennzeichen, Mandant, Nachtschicht-Zuordnung)
      // immer neu berechnen, nie aus dem Formular uebernehmen.
      const updatedFields = {
        driverId: merged.driverId,
        driverName: driverData.name || '',
        operatorId: driverData.operatorId || null,
        vehicleId: merged.vehicleId || null,
        vehiclePlate: vehicleData?.licensePlate || null,
        startTime: merged.startTime,
        plannedDuration: merged.plannedDuration,
        notes: updateData.notes ?? currentData.notes ?? '',
        ...schedule,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(shiftRef, updatedFields);
      
      return {
        id,
        ...currentData,
        ...updatedFields,
        driver: driverData,
        vehicle: vehicleData
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const cancelPlannedShift = createAsyncThunk(
  'plannedShifts/cancelPlannedShift',
  async ({ id, cancelDates = null }, { rejectWithValue }) => {
    try {
      const shiftRef = doc(db, 'plannedShifts', id);
      const shiftDoc = await getDoc(shiftRef);
      
      if (!shiftDoc.exists()) {
        throw new Error('Geplante Schicht nicht gefunden');
      }
      
      const currentData = shiftDoc.data();
      let changes;
      
      if (cancelDates && cancelDates.length > 0) {
        const remainingDates = (currentData.dates || []).filter(date => !cancelDates.includes(date));

        if (remainingDates.length === (currentData.dates || []).length) {
          throw new Error('Der gewählte Tag gehört nicht zu dieser Schicht');
        }
        
        changes = remainingDates.length === 0
          ? { status: 'cancelled', cancelledAt: new Date().toISOString() }
          : {
            // Nachtschicht-Zuordnung mitkuerzen, sonst bleibt die
            // Fortsetzung am Folgetag im Planer stehen.
            ...buildScheduleFields({
              dates: remainingDates,
              startTime: currentData.startTime,
              plannedDuration: currentData.plannedDuration
            }),
            updatedAt: new Date().toISOString()
          };
      } else {
        changes = { status: 'cancelled', cancelledAt: new Date().toISOString() };
      }

      await updateDoc(shiftRef, changes);
      
      return {
        id,
        ...currentData,
        ...changes
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deletePlannedShift = createAsyncThunk(
  'plannedShifts/deletePlannedShift',
  async (id, { rejectWithValue }) => {
    try {
      await deleteDoc(doc(db, 'plannedShifts', id));
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch actual shifts from mobile app structure for comparison
export const fetchActualShiftsForComparison = createAsyncThunk(
  'plannedShifts/fetchActualShifts',
  async ({ date }, { rejectWithValue }) => {
    try {
      const shiftDoc = await getDoc(doc(db, 'shifts', date));
      
      if (!shiftDoc.exists()) {
        return { date, drivers: {} };
      }
      
      const data = shiftDoc.data();
      return {
        date,
        drivers: data.drivers || {}
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  plannedShifts: [],
  actualShifts: {}, // For comparison with mobile app data
  isLoading: false,
  error: null,
  currentPlannedShift: null
};

const plannedShiftsSlice = createSlice({
  name: 'plannedShifts',
  initialState,
  reducers: {
    clearPlannedShiftsError: (state) => {
      state.error = null;
    },
    clearCurrentPlannedShift: (state) => {
      state.currentPlannedShift = null;
    },
    setActualShifts: (state, action) => {
      state.actualShifts[action.payload.date] = action.payload.data;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch planned shifts
      .addCase(fetchPlannedShifts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPlannedShifts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.plannedShifts = action.payload;
      })
      .addCase(fetchPlannedShifts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch planned shift by ID
      .addCase(fetchPlannedShiftById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPlannedShiftById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPlannedShift = action.payload;
      })
      .addCase(fetchPlannedShiftById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create planned shifts
      .addCase(createPlannedShifts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPlannedShifts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.plannedShifts.push(action.payload);
        state.currentPlannedShift = action.payload;
      })
      .addCase(createPlannedShifts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update planned shift
      .addCase(updatePlannedShift.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePlannedShift.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.plannedShifts.findIndex(shift => shift.id === action.payload.id);
        if (index !== -1) {
          state.plannedShifts[index] = action.payload;
        }
        if (state.currentPlannedShift?.id === action.payload.id) {
          state.currentPlannedShift = action.payload;
        }
      })
      .addCase(updatePlannedShift.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Cancel planned shift
      .addCase(cancelPlannedShift.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelPlannedShift.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.plannedShifts.findIndex(shift => shift.id === action.payload.id);
        if (index !== -1) {
          // Fahrer-/Fahrzeugdaten aus dem Laden behalten
          state.plannedShifts[index] = { ...state.plannedShifts[index], ...action.payload };
        }
      })
      .addCase(cancelPlannedShift.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete planned shift
      .addCase(deletePlannedShift.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deletePlannedShift.fulfilled, (state, action) => {
        state.isLoading = false;
        state.plannedShifts = state.plannedShifts.filter(shift => shift.id !== action.payload);
        if (state.currentPlannedShift?.id === action.payload) {
          state.currentPlannedShift = null;
        }
      })
      .addCase(deletePlannedShift.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch actual shifts
      .addCase(fetchActualShiftsForComparison.fulfilled, (state, action) => {
        state.actualShifts[action.payload.date] = action.payload.drivers;
      });
  }
});

export const { 
  clearPlannedShiftsError, 
  clearCurrentPlannedShift,
  setActualShifts 
} = plannedShiftsSlice.actions;

export default plannedShiftsSlice.reducer;